import React, { useCallback, useEffect, useMemo, useState } from 'react';
import client from '../../api/client';
import { diagnoseIfNotZipBlob } from '../../api/reportBinaryGuard';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useCache } from '../../hooks/useCache';
import RunNowDialog from './reporting/RunNowDialog';
import EventEditor from './reporting/EventEditor';
import './AdminPages.css';
import './AgenticReporting.css';

/**
 * Agentic Reporting (v2): two stacked sections on one admin page.
 *   1. Report Library — every registered IReportFunction; one Run Now per row.
 *   2. Scheduled Events — recurring bundles delivered to a CS rep on a schedule.
 *
 * Cron + email delivery are deliberately deferred: the model and UI know
 * everything they need, and once an IHostedService lands it can poll
 * `WHERE Enabled AND NextRunAt <= now` without further changes.
 */
function AgenticReporting() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('admin.reporting', true);
  const cache = useCache();
  const { customers } = useCustomerFilter();

  const [reports, setReports] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [csReps, setCsReps] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [runNowReport, setRunNowReport] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [busyEventId, setBusyEventId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reportsRes, eventsRes, usersRes, csRepsRes] = await Promise.all([
        client.get('/api/reporting/reports'),
        client.get('/api/reporting/events'),
        client.get('/api/admin/users').catch(() => ({ data: { users: [] } })),
        client.get('/api/reporting/cs-reps').catch(() => ({ data: { reps: [] } })),
      ]);
      setReports(reportsRes.data?.reports || []);
      setEvents(eventsRes.data?.events || []);
      setUsers(usersRes.data?.users || []);
      setCsReps(csRepsRes.data?.reps || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet, fine */ }
    })();
    // useCache() returns a fresh object each render, so we deliberately
    // run this once on mount — same pattern as Insights/QuickRefer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMap = useMemo(() => {
    const m = new Map();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const handleRunEventNow = async (ev) => {
    setBusyEventId(ev.id);
    setError('');
    try {
      const res = await client.post(`/api/reporting/events/${ev.id}/run`, {}, { responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const bad = await diagnoseIfNotZipBlob(blob);
      if (bad) {
        throw new Error(`${bad} (expected a .zip)`);
      }
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const fileName = m ? m[1] : `event-${ev.id}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      reload();
    } catch (err) {
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch { /* leave msg */ }
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      setError(msg || 'Event run failed');
    } finally {
      setBusyEventId(null);
    }
  };

  const handleToggleEnabled = async (ev, enabled) => {
    try {
      await client.patch(`/api/reporting/events/${ev.id}/enabled`, { enabled });
      reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to toggle event');
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete event "${ev.name}"?`)) return;
    try {
      await client.delete(`/api/reporting/events/${ev.id}`);
      reload();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete event');
    }
  };

  if (loading) {
    return <div className="agentic-page"><div className="info-box">Loading reporting data…</div></div>;
  }

  return (
    <div className="agentic-page">
      <header className="agentic-header">
        <h1>Agentic Reporting</h1>
        <p className="agentic-subtitle">
          Generate reports on demand from the Library, or schedule recurring
          bundles for a CS rep. Cron and email delivery will plug in here next.
        </p>
      </header>

      {error && <div className="agentic-error">{error}</div>}

      {/* ---- Section A: Report Library ---- */}
      <section className="agentic-section">
        <header className="agentic-section-head">
          <h2>Report Library</h2>
          <span className="agentic-section-sub">
            {reports.length} registered report{reports.length === 1 ? '' : 's'}
          </span>
        </header>

        {reports.length === 0 ? (
          <div className="info-box">
            No report functions are registered yet. Add an <code>IReportFunction</code>
            in <code>backend/Reports/Reports/</code> and register it in <code>Program.cs</code>.
          </div>
        ) : (
          <table className="agentic-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Format</th>
                <th className="agentic-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id}>
                  <td className="agentic-cell-name">
                    {r.displayName}
                    {r.parameters?.containsPhi && (
                      <span className="agentic-phi-pill" title="Unmasked PHI — Run Now download only">PHI</span>
                    )}
                  </td>
                  <td className="agentic-cell-desc">{r.description}</td>
                  <td>
                    <span className="agentic-format-pill">{(r.outputFormat || '').toUpperCase()}</span>
                    {r.parameters?.excludesFromScheduledEvents && (
                      <span className="agentic-runonly-pill" title="Cannot be scheduled for email">Run Now only</span>
                    )}
                  </td>
                  <td className="agentic-col-actions">
                    <button
                      type="button"
                      className="agentic-btn-primary agentic-btn-small"
                      onClick={() => setRunNowReport(r)}
                      disabled={!canEdit}
                      title={canEdit ? 'Run this report now' : 'You need admin.reporting:Edit'}
                    >▶ Run Now</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- Section B: Scheduled Events ---- */}
      <section className="agentic-section">
        <header className="agentic-section-head">
          <h2>Scheduled Events</h2>
          <button
            type="button"
            className="agentic-btn-primary agentic-btn-small"
            disabled={!canEdit}
            onClick={() => { setEditingEvent(null); setEditorOpen(true); }}
          >+ New Event</button>
        </header>

        {events.length === 0 ? (
          <div className="info-box">
            No scheduled events yet. Click <strong>+ New Event</strong> to bundle
            one or more reports for a CS rep on a recurring schedule.
          </div>
        ) : (
          <table className="agentic-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Recipient</th>
                <th>Schedule</th>
                <th className="agentic-col-num">Items</th>
                <th>Last Run</th>
                <th>Next Run</th>
                <th className="agentic-col-toggle">Enabled</th>
                <th className="agentic-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => {
                const recipient = userMap.get(ev.recipientUserId);
                return (
                  <tr key={ev.id} className={ev.enabled ? '' : 'is-disabled'}>
                    <td className="agentic-cell-name">{ev.name}</td>
                    <td>
                      {recipient
                        ? `${(recipient.firstName || '')} ${(recipient.lastName || '')}`.trim() || recipient.email
                        : (ev.recipientName || ev.recipientUserId || '—')}
                    </td>
                    <td>{describeSchedule(ev)}</td>
                    <td className="agentic-col-num">{ev.items?.length || 0}</td>
                    <td>{ev.lastRunAt ? new Date(ev.lastRunAt).toLocaleString() : '—'}</td>
                    <td>{ev.nextRunAt ? new Date(ev.nextRunAt).toLocaleString() : '—'}</td>
                    <td className="agentic-col-toggle">
                      <label className="agentic-switch">
                        <input
                          type="checkbox"
                          checked={ev.enabled !== false}
                          onChange={(e) => handleToggleEnabled(ev, e.target.checked)}
                          disabled={!canEdit}
                        />
                        <span className="agentic-switch-slider"></span>
                      </label>
                    </td>
                    <td className="agentic-col-actions">
                      <button
                        type="button"
                        className="agentic-btn-secondary agentic-btn-small"
                        disabled={!canEdit}
                        onClick={() => { setEditingEvent(ev); setEditorOpen(true); }}
                      >Edit</button>
                      <button
                        type="button"
                        className="agentic-btn-primary agentic-btn-small"
                        disabled={!canEdit || busyEventId === ev.id || (ev.items?.length || 0) === 0}
                        onClick={() => handleRunEventNow(ev)}
                        title="Run all items in this event now (downloads ZIP)"
                      >{busyEventId === ev.id ? '…' : '▶ Run Now'}</button>
                      <button
                        type="button"
                        className="agentic-btn-danger agentic-btn-small"
                        disabled={!canEdit}
                        onClick={() => handleDelete(ev)}
                      >Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {runNowReport && (
        <RunNowDialog
          report={runNowReport}
          customers={customers}
          savedFilters={savedFilters}
          csReps={csReps}
          onClose={() => setRunNowReport(null)}
        />
      )}

      <EventEditor
        open={editorOpen}
        initialEvent={editingEvent}
        reports={reports}
        customers={customers}
        savedFilters={savedFilters}
        csReps={csReps}
        users={users}
        onClose={() => { setEditorOpen(false); setEditingEvent(null); }}
        onSaved={() => reload()}
      />
    </div>
  );
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describeSchedule(ev) {
  if (!ev?.scheduleType) return '—';
  const cfg = ev.scheduleConfig || {};
  const tz = ev.timeZone || '';
  const tzShort = tz.split('/').pop()?.replace('_', ' ');
  const time = `${String(cfg.hour ?? 7).padStart(2, '0')}:${String(cfg.minute ?? 0).padStart(2, '0')}`;
  switch (ev.scheduleType) {
    case 'Daily':     return `Daily · ${time}${tzShort ? ` ${tzShort}` : ''}`;
    case 'Weekly':    return `Weekly · ${DAY_LABELS[cfg.dayOfWeek ?? 1]} ${time}${tzShort ? ` ${tzShort}` : ''}`;
    case 'Monthly':   return `Monthly · day ${cfg.dayOfMonth ?? 1} at ${time}${tzShort ? ` ${tzShort}` : ''}`;
    case 'Quarterly': return `Quarterly · wk ${cfg.weekOfQuarter ?? 1}, ${DAY_LABELS[cfg.dayOfWeek ?? 1]} ${time}${tzShort ? ` ${tzShort}` : ''}`;
    case 'OneTime':   return cfg.runAt ? `Once · ${new Date(cfg.runAt).toLocaleString()}` : 'Once';
    default:          return ev.scheduleType;
  }
}

export default AgenticReporting;
